import { SearchListingsOutput } from "artifacts/SearchListingsOutput";
import { SearchSimilarNearbyHomesOutput } from "artifacts/SearchSimilarNearbyHomesOutput";
import { SearchSchoolsOutput } from "artifacts/SearchSchoolsOutput";
import { createContext, createElement, useContext } from "react";
import { defaultSchema } from "rehype-sanitize";
import { IArtifactComponentProps, IArtifactComponentTextProps, IMessage } from "types/services";
import { Element as HastElement } from "hast";
import { Schema } from "rehype-sanitize/lib";
import { ExtraProps } from "react-markdown";
import { PhotoPropertyDetailsOutput } from "artifacts/PhotoPropertyDetailsOutput";
import { InteriorPropertyDetailsOutput } from "artifacts/InteriorPropertyDetailsOutput";
import { ExteriorPropertyDetailsOutput } from "artifacts/ExteriorPropertyDetailsOutput";
import { RoomPropertyDetailsOutput } from "artifacts/RoomPropertyDetailsOutput";
import { AgentDetailsOutput } from "artifacts/AgentDetailsOutput";
import { SearchSchoolsListOutput } from "artifacts/SearchSchoolsListOutput";
import { AgentListOutput } from "artifacts/AgentListOutput";
import { PropertyDetailsOutput } from "artifacts/PropertyDetailsOutput";
import { SimilarListingOutput } from "artifacts/SimilarListingOutput";

// For Copy Markdown
import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkMdx from 'remark-mdx'
import { visit } from 'unist-util-visit'
import type { Root } from 'mdast'
import { SearchListingsFilters } from "artifacts/SearchListingsFilters";
// import rehypeRaw from "rehype-raw";
// import remarkRehype from 'remark-rehype'
// import remarkGfm from 'remark-gfm'
//import { toMarkdown } from 'mdast-util-to-markdown'
//import { VFile } from 'vfile'
//import { toMdast } from 'hast-util-to-mdast';

type IArtifactComponent<T> = (props: IArtifactComponentProps<T, any>) => JSX.Element;

interface IStaticArtifactComponent<T> extends IArtifactComponent<T> {
  artifactKey: string;
  asText(props: IArtifactComponentTextProps<T, any>): string;
}

export type IArtifactMap = Record<string, IStaticArtifactComponent<any>>;

/**
 * Everything an artifact component needs besides its own tag attributes: the
 * artifact bag it resolves itself from, and the message it belongs to.
 *
 * This travels through context rather than through the component map, which is
 * the whole point of the indirection. The map used to be rebuilt per render so
 * these values could be closed over; that handed react-markdown a NEW component
 * function for every tag on every render, and a changed element `type` makes
 * React unmount and remount the subtree instead of updating it. Every artifact
 * in the transcript was torn down and rebuilt on each keystroke, navigation and
 * `isSending` flip -- the filter chips visibly blanked and refilled each time
 * (CB-1780). Context changes re-render consumers; they never change identity.
 */
export interface IChatArtifactContext {
  artifacts: Record<string, any[]>;
  onNewMessage: IArtifactComponentProps<{}, any>["onNewMessage"];
  message?: IMessage;
}

// A default rather than `undefined`, so an artifact component rendered outside a
// provider degrades to "no artifact found" (which it already handles) instead of
// throwing.
const ChatArtifactContext = createContext<IChatArtifactContext>({
  artifacts: {},
  onNewMessage: () => {},
});

export const ChatArtifactProvider = ChatArtifactContext.Provider;

// Custom component tags that may legitimately appear inside a USER message.
// A user's message body is untrusted free-text, so only the filter-chips
// component is permitted there; any other component tag a user types (or any
// <script>/<iframe>/event handler) is stripped by rehype-sanitize.
const USER_ALLOWED_TAGS: string[] = ["search-listings-filters"];

// Identifier attributes our custom component tags carry — the props each
// component reads to resolve its artifact.
//
// IMPORTANT: rehype-sanitize matches attribute names EXACTLY. There is NO "*"
// wildcard for attributes — a "*" entry matches only an attribute literally
// named "*". So `[tag]: ["*"]` strips EVERY attribute, leaving the component
// mounted with no id and rendering empty. These names must be listed explicitly.
const CUSTOM_TAG_ATTRS: string[] = ["search_id", "harid", "school_id"];

// Extend rehype-sanitize's GitHub-safe default schema to also allow the given
// custom component tag names (each with the identifier attributes above). The
// tags map to React components with no raw-DOM sink, so this cannot introduce
// an XSS vector — it only lets our own components mount.
function buildSchema(customTags: string[]): Schema {
  return {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), ...customTags],
    attributes: {
      ...defaultSchema.attributes,
      ...customTags.reduce(
        (acc, key) => ({ ...acc, [key]: CUSTOM_TAG_ATTRS }),
        {}
      ),
    },
  };
}

const tagsMap: Record<
  string,
  (input: React.HTMLAttributes<any> & ExtraProps) => any
> = {
  ul: visitList,
  ol: visitList,
};

const artifactsMap: IArtifactMap = {
  "search-listings-output": SearchListingsOutput,
  "search-similar-nearby-homes-output": SearchSimilarNearbyHomesOutput,
  "similar-listings-output": SimilarListingOutput,
  "search-schools-output": SearchSchoolsListOutput,
  "property-assigned-schools-output": SearchSchoolsOutput,
  "property-photo-output": PhotoPropertyDetailsOutput,
  "property-interior-output": InteriorPropertyDetailsOutput,
  "property-exterior-output": ExteriorPropertyDetailsOutput,
  "property-rooms-output": RoomPropertyDetailsOutput,
  "property-agent-output": AgentDetailsOutput,
  "search-agent-output": AgentListOutput,
  "property-detail-output": PropertyDetailsOutput,
  "search-listings-filters": SearchListingsFilters,
};

/**
 * Wrap one artifact component in the `{ node }` shape react-markdown hands a
 * custom tag. Built once per artifact component at module load, so the function
 * identity react-markdown sees is a constant.
 */
function bindComponent(Component: IStaticArtifactComponent<any>) {
  const { artifactKey } = Component;

  return function BoundArtifact({ node }: Record<"node", HastElement>) {
    const { artifacts, onNewMessage, message } = useContext(ChatArtifactContext);
    const args = node.properties;

    return (
      <Component
        {...args}
        artifacts={artifacts?.[artifactKey]}
        onNewMessage={onNewMessage}
        message={message}
      />
    );
  };
}

const customComponents: Record<string, IArtifactComponent<any>> = Object.keys(
  artifactsMap
).reduce(
  (acc, key) => ({ ...acc, [key]: bindComponent(artifactsMap[key]) }),
  {}
);

const customTags = Object.keys(customComponents);

// Only USER messages are sanitized. An assistant message is server-generated
// and its artifact tags are how artifacts get mounted, so it renders with
// rehypeRaw alone; a user's message body is untrusted free-text, so only the
// components in USER_ALLOWED_TAGS survive here and every other tag (scripts,
// iframes, other components, event handlers) is stripped, which is what blocks
// HTML/JSX injection from the textbox.
//
// It depends only on the static tag list, so it is built once rather than per
// render: a fresh schema object each render re-ran rehype-sanitize's setup for
// no reason, and made the `rehypePlugins` array churn too.
export const userSchema = buildSchema(
  customTags.filter((tag) => USER_ALLOWED_TAGS.includes(tag))
);

/**
 * External links open in a new tab and must not bubble their click to the
 * wrappers around a message. Defined at module scope for the same reason the
 * artifact components are: an inline override would reintroduce the per-render
 * identity churn this module exists to avoid.
 */
function ExternalLink({ node, ...props }: any) {
  const rel = props.rel ? `${props.rel} noopener noreferrer` : "noopener noreferrer";
  return <a {...props} target="_blank" rel={rel} onClick={(e) => e.stopPropagation()} />;
}

/** Stable component map for `<ReactMarkdown components={...}>`. */
export const chatArtifactComponents: Record<string, (...props: any[]) => any> = {
  ...tagsMap,
  ...customComponents,
  a: ExternalLink,
};

function visitList(input: React.HTMLAttributes<HTMLUListElement> & ExtraProps) {
  const { node, ...props } = input;
  const children = (node!.children?.filter((x) => x.type === "element") ??
    []) as HastElement[];

  const listOfImages = children?.every((child) => {
    const elements = child.children.filter(
      (x) => x.type === "element"
    ) as HastElement[];
    return elements.length === 1 && elements[0].tagName === "img";
  });

  const newProps = { ...props };
  if (listOfImages) {
    const className = "image-list";
    if (newProps.className) {
      newProps.className = `${newProps.className} ${className}`;
    } else {
      newProps.className = className;
    }
  }

  return createElement(node!.tagName, newProps);
}

export function buildCopyMarkdown(raw: string, artifactsList: Record<string, any[]>) {
  const tree = unified()
    .use(remarkParse)
    .use(remarkMdx)
    .parse(raw) as Root

  return replaceCustomComponents(raw, tree, artifactsList)
}

function replaceCustomComponents(raw: string, tree: Root, artifactsList: Record<string, any[]>) {
  visit(tree, (node, index, parent) => {
    //console.log({ node, index, parent });
    if (!node.position) { return; }

    if (node.type === 'mdxJsxFlowElement' || node.type === 'mdxJsxTextElement') {
      const Component = node.name ? artifactsMap[node.name] : undefined;
      if (!Component) { return; }

      const { artifactKey } = Component;
      const artifacts = artifactsList?.[artifactKey];
      const properties = node.attributes?.reduce((acc, attr) => {
        if (attr.type === 'mdxJsxAttribute' && attr.name) {
          acc[attr.name] = (attr.value as string) || true;
        }
        return acc;
      }, {} as Record<string, any>) || {};

      const value = Component.asText({ ...properties, artifacts });
      //parent!.children[index!] = { type: 'text', value };
      const { start, end } = node.position;
      raw = raw.substring(0, start.offset!) + value + raw.substring(end.offset!);
    }
  });

  return raw;
}

// export async function buildCopyMarkdown(raw: string, artifactsList: Record<string, any[]>) {
//   const processor = unified()
//     .use(remarkParse)
//     .use(remarkRehype, { allowDangerousHtml: true })
//     .use(rehypeRaw)
//   //.use(replaceCustomComponents);
//   //.parse(raw) as Root

//   const file = new VFile()
//   file.value = raw
//   const tree = await processor.run(processor.parse(file), file)
//   replaceCustomComponents(tree, artifactsList)

//   const mdast = toMdast(tree);
//   return toMarkdown(mdast)
// }

// function replaceCustomComponents(tree: Root, artifactsList: Record<string, any[]>) {
//   visit(tree, (node, index, parent) => {
//     if (node.type === 'element') {
//       const Component = artifactsMap[node.tagName];
//       if (!Component) { return; }

//       const { artifactKey } = Component;
//       const artifacts = artifactsList?.[artifactKey];
//       const value = Component.asText({ ...node.properties, artifacts });
//       parent!.children[index!] = { type: 'text', value };
//     }
//   });
// }